// The settings dialog shell: 224px nav column + panel, per Figma node 700:948.
//
// Wraps the shared ui/Modal rather than hand-rolling a dialog, so it inherits
// the portal, Tab trap, focus restoration, body-scroll lock, dialog ARIA and
// the canvas keyboard-shortcut guard. It supplies its own chrome (full-bleed
// split layout, per-panel header and close button) via Modal's additive
// `padded` / `hideCloseButton` / `surfaceClassName` props.
//
// Section switching and closing both route through the active section's
// SectionController, so a section with unsaved edits can interpose its discard
// confirmation. The remount `key` changes only AFTER that resolves — otherwise
// the draft would be destroyed before the user could answer.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'

import Modal from '../../ui/Modal'
import { useAuth } from '../../../contexts/AuthContext'
import type { User } from '../../../lib/gotrueTypes'
import { useBooleanFlagValue } from '../../../hooks/useBooleanFlagValue'
import { useTeamsEnabled } from '../../../features/teams/useTeamsEnabled'
import { canSeeBilling } from '../../../features/billing/billingAccess'
import { cn } from '../../../lib/utils'
import { settingsViewEntryPoint } from '../../../analytics/settingsAnalytics'
import { createNadDebug } from '../../../utils/debugLog'
import ProfileContent from '../../auth/ProfileContent'
import ChangePasswordModal from '../../auth/ChangePasswordModal'
import DeleteAccountModal from '../../auth/DeleteAccountModal'
import UserRulesContent from '../../rules/UserRulesContent'
import TeamsSection from '../../teams/TeamsSection'
import SettingsForm from '../SettingsForm'
import UsageAndCreditsSection from './sections/UsageAndCreditsSection'
import SettingsNav from './SettingsNav'
import { SETTINGS_SECTIONS } from './sectionRegistry'
import { useSettingsModal } from './SettingsModalProvider'
import type { SectionController } from '../sectionController'
import type { SettingsSectionId } from './settingsSections'

const layoutLog = createNadDebug('dw:settings-layout:panel')

export default function SettingsModal() {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { isOpen, activeSection, open, close, entryPoint, teamsView } = useSettingsModal()

  // The section the user reached through the nav column during THIS open, for
  // the Teams invite events' entry_point (RFC-1577). The provider's entryPoint
  // only knows how the dialog was opened; a nav click re-uses `open()` and so
  // cannot be told apart there.
  const [navigatedTo, setNavigatedTo] = useState<SettingsSectionId | null>(null)
  useEffect(() => {
    if (!isOpen) setNavigatedTo(null)
  }, [isOpen])

  const controllerRef = useRef<SectionController>(null)
  const activeNavItemRef = useRef<HTMLButtonElement>(null)
  const panelHeadingRef = useRef<HTMLHeadingElement>(null)
  const panelContentRef = useRef<HTMLDivElement>(null)

  // Layout-only diagnostics: observe both panel resizes and asynchronously
  // loaded model rows. Never send names, endpoint URLs or key values to logs.
  useEffect(() => {
    if (!import.meta.env.DEV || !isOpen) return
    const content = panelContentRef.current
    if (!content) return
    let frame = 0
    let previous = ''
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const { clientWidth, scrollWidth } = content
        const signature = `${clientWidth}:${scrollWidth}`
        if (signature === previous || clientWidth === 0) return
        previous = signature
        layoutLog({
          event: 'settings-layout:panel-measured',
          trace_id: `settings-layout:${activeSection}`,
          section: activeSection,
          client_width: clientWidth,
          scroll_width: scrollWidth,
          overflow: scrollWidth > clientWidth,
        })
      })
    }
    const resize = new ResizeObserver(measure)
    const mutations = new MutationObserver(measure)
    resize.observe(content)
    mutations.observe(content, { childList: true, subtree: true, characterData: true })
    measure()
    return () => {
      cancelAnimationFrame(frame)
      resize.disconnect()
      mutations.disconnect()
    }
  }, [isOpen, activeSection])
  const [childDialog, setChildDialog] = useState<'none' | 'change-password' | 'delete-account'>(
    'none',
  )

  const billingSandboxEnabled = useBooleanFlagValue('billing_sandbox_enabled', false)
  const teamsEnabled = useTeamsEnabled()
  const sections = useMemo(
    () =>
      SETTINGS_SECTIONS.filter((s) =>
        s.isVisible({
          canSeeBilling: canSeeBilling(user),
          billingSandboxEnabled,
          teamsEnabled,
        }),
      ),
    [user, billingSandboxEnabled, teamsEnabled],
  )

  const current = sections.find((s) => s.id === activeSection) ?? sections[0]

  // Ask the active section for permission before moving. Sections with nothing
  // unsaved resolve immediately; a dirty one shows its own confirmation and
  // resolves when the user answers.
  const requestLeave = useCallback(async (target: SettingsSectionId | 'close') => {
    const controller = controllerRef.current
    if (!controller) return true
    return controller.requestTransition(target)
  }, [])

  const handleSelect = useCallback(
    (id: SettingsSectionId) => {
      if (id === activeSection) return
      void requestLeave(id).then((allow) => {
        if (!allow) return
        setNavigatedTo(id)
        open(id)
      })
    },
    [activeSection, open, requestLeave],
  )

  const handleClose = useCallback(() => {
    const controller = controllerRef.current
    // Clean close stays synchronous — see UserRulesModal for why deferring it
    // to a microtask is user-visible.
    if (!controller || !controller.isDirty) {
      void controller?.requestTransition('close')
      close()
      return
    }
    void controller.requestTransition('close').then((allow) => {
      if (allow) close()
    })
  }, [close])

  const handleSignOut = useCallback(() => {
    void requestLeave('close').then(async (allow) => {
      if (!allow) return
      close()
      await signOut()
      navigate('/')
    })
  }, [close, navigate, requestLeave, signOut])

  // Change Password / Delete Account are SEQUENTIAL, never nested: close this
  // dialog, open the child, reopen on Profile afterwards. Two live <Modal>s
  // would each install Escape/Tab handlers and scroll locking, so one Escape
  // could dismiss both and closing one would unlock scrolling behind the other.
  const openChild = useCallback(
    (which: 'change-password' | 'delete-account') => {
      close()
      setChildDialog(which)
    },
    [close],
  )
  const closeChild = useCallback(() => {
    setChildDialog('none')
    open('profile')
  }, [open])

  // ui/Modal traps Tab but never sets INITIAL focus. Put the user on the active
  // nav row when the dialog opens, and on the panel heading when the section
  // changes, so a keyboard user isn't stranded at the top of the document.
  useEffect(() => {
    if (!isOpen) return
    activeNavItemRef.current?.focus()
  }, [isOpen])
  const firstRenderRef = useRef(true)
  useEffect(() => {
    if (!isOpen) {
      firstRenderRef.current = true
      return
    }
    if (firstRenderRef.current) {
      firstRenderRef.current = false
      return
    }
    panelHeadingRef.current?.focus()
  }, [isOpen, activeSection])

  // `user` is narrowed HERE and passed in, rather than re-read inside
  // renderSection where the guard no longer applies — that needed a `user!`
  // which silently opted the ProfileContent prop out of type checking.
  const body = user && current ? renderSection(current.id, user) : null

  // Read by the section ONCE, at mount (sections remount per activation).
  // `teamsView` is cleared by TeamsSection right after mount, so a later
  // render would otherwise relabel a profile-flyout open as a menu open.
  function sectionEntryPoint(id: SettingsSectionId) {
    return settingsViewEntryPoint({
      dialogEntryPoint: entryPoint,
      navigated: navigatedTo === id,
      fromProfileFlyout: id === 'teams' && teamsView === 'create',
    })
  }

  function renderSection(id: SettingsSectionId, currentUser: User) {
    switch (id) {
      case 'profile':
        return (
          <ProfileContent
            active
            user={currentUser}
            onChangePassword={() => openChild('change-password')}
            onDeleteAccount={() => openChild('delete-account')}
          />
        )
      case 'general':
        // `gatewayOnly` is REQUIRED. Without it SettingsForm also renders the
        // API endpoint, Supabase config, JWT copy control and API-key manager —
        // developer configuration, not account settings.
        return <SettingsForm compact gatewayOnly onSave={handleClose} />
      case 'personalization':
        return <UserRulesContent ref={controllerRef} active surface="dashboard" />
      case 'teams':
        return <TeamsSection entryPoint={sectionEntryPoint('teams')} />
      case 'usage-credits':
        return <UsageAndCreditsSection />
      default:
        return null
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen && user != null}
        onClose={handleClose}
        size="settings"
        padded={false}
        hideCloseButton
        surfaceClassName="rounded-[32px] border border-white/[0.07] bg-[rgba(18,18,21,0.9)] backdrop-blur-[20px] shadow-[0px_24px_24px_rgba(0,0,0,0.5)]"
      >
        <div
          className="flex h-[600px] max-h-[85vh] w-full flex-col sm:flex-row"
          data-testid="settings-modal"
        >
          <SettingsNav
            visible={isOpen}
            sections={sections}
            activeSection={current?.id ?? 'profile'}
            onSelect={handleSelect}
            onSignOut={handleSignOut}
            activeItemRef={activeNavItemRef}
          />

          <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-surface-0" data-testid="settings-panel">
            <div className="flex shrink-0 items-start gap-3 px-4 py-4">
              <h3
                ref={panelHeadingRef}
                tabIndex={-1}
                className="min-h-7 flex-1 text-xl font-semibold leading-[25px] text-white outline-none"
                data-testid="settings-panel-title"
              >
                {current ? t(current.labelKey) : ''}
              </h3>
              <button
                type="button"
                onClick={handleClose}
                data-testid="settings-modal-close"
                aria-label={t('settingsModal.close')}
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.12]',
                  'text-white transition-colors hover:bg-white/20',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                )}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* `key` remounts the section on change so no stale draft, error or
                timer survives a tab switch. Safe here precisely because the
                transition guard has already resolved by this point. */}
            <div
              key={current?.id ?? 'none'}
              ref={panelContentRef}
              data-testid="settings-panel-content"
              className="min-h-0 min-w-0 flex-1 overflow-y-auto px-4 pb-4"
            >
              {body}
            </div>
          </div>
        </div>
      </Modal>

      <SettingsChildDialogs which={childDialog} onClose={closeChild} />
    </>
  )
}

/** Change Password / Delete Account, kept out of the shell's render path. */
function SettingsChildDialogs({
  which,
  onClose,
}: {
  which: 'none' | 'change-password' | 'delete-account'
  onClose: () => void
}) {
  const { user, hasPasswordIdentity } = useAuth()
  const email = user?.email ?? ''
  return (
    <>
      <ChangePasswordModal
        isOpen={which === 'change-password'}
        onClose={onClose}
        email={email}
        hasPasswordIdentity={hasPasswordIdentity}
      />
      <DeleteAccountModal
        isOpen={which === 'delete-account'}
        onClose={onClose}
        email={email}
      />
    </>
  )
}
